// src/Context/StreakContext.tsx
// The daily streak for the whole app: the GET /api/streak summary (count,
// week strip, freezes, milestones), and the once-a-day celebration. Activity
// completion responses carry a `streak` object; they reach here through
// `celebrateIfExtended(streak)` or the `reportStreak` event bridge
// (Context/streakEvents.ts), so the moment shows wherever the activity ended.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { fetchStreakSummary, postStreakActivity } from "../Components/Streak/streakApi";
import { StreakResult, StreakSummary } from "../Components/Streak/streakModel";
import StreakCelebration from "../Components/Streak/StreakCelebration";
import { STREAK_EVENT } from "./streakEvents";

export interface StreakInfo {
  count: number;
  /** Legacy: weekday names of the current run's last seven days. */
  days: string[];
  /** ISO timestamp of the last qualifying activity. */
  lastUpdated: string | null;
  /** True once today's activity has been done. */
  extendedToday: boolean;
  summary: StreakSummary | null;
  loading: boolean;
  error?: unknown;
  /** Re-fetch from the server. */
  refresh: () => Promise<void>;
  /** Optimistic update if you already know the new value. */
  mutate: (count: number, days: string[]) => void;
  /** Show the celebration when `streak.extendedToday`; resolves when it closes (at once otherwise). */
  celebrateIfExtended: (streak?: Partial<StreakResult> | null) => Promise<void>;
  /** Record a lesson unit end through the legacy endpoint and celebrate. */
  recordActivity: () => Promise<StreakResult | null>;
  /** Resolves when the current celebration closes; waits up to graceMs for one to start. */
  waitForCelebration: (graceMs?: number) => Promise<void>;
  celebrating: boolean;
}

const noop = async () => {};
const defaultValue: StreakInfo = {
  count: 0,
  days: [],
  lastUpdated: null,
  extendedToday: false,
  summary: null,
  loading: true,
  refresh: noop,
  mutate: () => {},
  celebrateIfExtended: noop,
  recordActivity: async () => null,
  waitForCelebration: noop,
  celebrating: false,
};

const StreakContext = createContext<StreakInfo>(defaultValue);

const SHOWN_KEY = "moonrock-streak-celebrated";
/** Set by the older e2e suites (e2e/helpers.ts signIn) so the daily moment does not cover their screens. */
const OFF_KEY = "moonrock-streak-celebration-off";

function isResult(x: any): x is StreakResult {
  return !!x && typeof x === "object" && typeof x.count === "number" && typeof x.extendedToday === "boolean";
}

export const StreakProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<StreakSummary | null>(null);
  const [count, setCount] = useState(0);
  const [days, setDays] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>();
  const [celebration, setCelebration] = useState<StreakResult | null>(null);

  const activeRef = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null);
  const shownRef = useRef<Set<string>>(new Set());

  const fetchStreak = useCallback(async () => {
    if (!localStorage.getItem("accessToken")) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const s = await fetchStreakSummary();
      if (s && typeof s.count === "number") {
        setSummary(s);
        setCount(s.count);
        setDays(s.count > 0 && Array.isArray(s.streakDays) ? s.streakDays : []);
      }
      setError(undefined);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const mutate = useCallback((newCount: number, newDays: string[]) => {
    setCount(newCount);
    setDays(newDays);
  }, []);

  /** Fold an activity result into local state right away; the summary refetch follows. */
  const absorb = useCallback((r: StreakResult) => {
    setCount(r.count);
    if (Array.isArray(r.streakDays)) setDays(r.streakDays);
    setSummary((s) =>
      s
        ? {
            ...s,
            count: r.count,
            longest: r.longest ?? s.longest,
            totalDays: r.totalDays ?? s.totalDays,
            extendedToday: s.extendedToday || r.extendedToday,
            freezes: r.freezes ?? s.freezes,
            weekStrip: r.weekStrip || s.weekStrip,
            nextMilestone: r.nextMilestone ?? s.nextMilestone,
          }
        : s,
    );
  }, []);

  const celebrateIfExtended = useCallback(
    (streak?: Partial<StreakResult> | null): Promise<void> => {
      const active = activeRef.current?.promise;
      if (!isResult(streak)) return active ?? Promise.resolve();
      absorb(streak);
      if (!streak.extendedToday) {
        void fetchStreak();
        return active ?? Promise.resolve();
      }
      const key = `${streak.todayKey}:${streak.count}`;
      let stored: string | null = null;
      let off = false;
      try {
        stored = localStorage.getItem(SHOWN_KEY);
        off = localStorage.getItem(OFF_KEY) === "1";
      } catch {
        stored = null;
      }
      if (off || shownRef.current.has(key) || stored === key) return active ?? Promise.resolve();
      shownRef.current.add(key);
      try {
        localStorage.setItem(SHOWN_KEY, key);
      } catch {
        /* private mode */
      }
      if (active) return active;
      let resolve: () => void = () => {};
      const promise = new Promise<void>((r) => {
        resolve = r;
      });
      activeRef.current = { promise, resolve };
      setCelebration(streak);
      return promise;
    },
    [absorb, fetchStreak],
  );

  const close = useCallback(() => {
    setCelebration(null);
    const a = activeRef.current;
    activeRef.current = null;
    a?.resolve();
    void fetchStreak();
  }, [fetchStreak]);

  const openStreak = useCallback(() => {
    close();
    navigate("/streak");
  }, [close, navigate]);

  const recordActivity = useCallback(async () => {
    try {
      const r = await postStreakActivity();
      if (r) void celebrateIfExtended(r);
      return r;
    } catch {
      return null;
    }
  }, [celebrateIfExtended]);

  const waitForCelebration = useCallback(async (graceMs = 0) => {
    const started = Date.now();
    while (!activeRef.current && Date.now() - started < graceMs) {
      await new Promise((r) => setTimeout(r, 100));
    }
    if (activeRef.current) await activeRef.current.promise;
  }, []);

  useEffect(() => {
    if (localStorage.getItem("accessToken")) void fetchStreak();
    else setLoading(false);
  }, [fetchStreak]);

  useEffect(() => {
    const onFocus = () => void fetchStreak();
    const onStorage = (e: StorageEvent) => {
      if (e.key === "accessToken" && e.newValue) void fetchStreak();
    };
    const onStreak = (e: Event) => void celebrateIfExtended((e as CustomEvent).detail);
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    window.addEventListener(STREAK_EVENT, onStreak);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(STREAK_EVENT, onStreak);
    };
  }, [fetchStreak, celebrateIfExtended]);

  const value = useMemo<StreakInfo>(
    () => ({
      count,
      days,
      lastUpdated: summary?.lastUpdated ?? null,
      extendedToday: !!summary?.extendedToday,
      summary,
      loading,
      error,
      refresh: fetchStreak,
      mutate,
      celebrateIfExtended,
      recordActivity,
      waitForCelebration,
      celebrating: !!celebration,
    }),
    [count, days, summary, loading, error, fetchStreak, mutate, celebrateIfExtended, recordActivity, waitForCelebration, celebration],
  );

  return (
    <StreakContext.Provider value={value}>
      {children}
      {celebration && <StreakCelebration result={celebration} onClose={close} onOpenStreak={openStreak} />}
    </StreakContext.Provider>
  );
};

/** Convenience hook */
export const useStreak = () => useContext(StreakContext);
